import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Mail, Menu, CalendarCheck, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CONTACT, SERVICES, MAIN_SERVICES } from "@/lib/content";
import { WhatsAppIcon, InstagramIcon } from "./Icons";

const navCls = ({ isActive }) =>
  `whitespace-nowrap font-brand text-[11px] tracking-[0.14em] uppercase transition-colors hover:text-granate ${isActive ? "text-granate" : "text-ink/80"}`;

export const Logo = ({ light = false, testId = "header-logo" }) => (
  <Link to="/" className="flex items-center gap-3" data-testid={testId}>
    <img src="/brand/sello-sm.webp" alt="La Patrona" className="w-12 h-12 rounded-full object-cover" />
    <span className="leading-tight whitespace-nowrap">
      <span className={`block font-brand tracking-[0.3em] text-base ${light ? "text-white" : "text-ink"}`}>LA PATRONA</span>
      <span className={`block font-brand text-[9px] tracking-[0.3em] uppercase ${light ? "text-white/60" : "text-ink/60"}`}>Eventos · Catering</span>
    </span>
  </Link>
);

const TopBar = () => (
  <div className="bg-blush/80 backdrop-blur border-b border-rose/20">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 h-10 flex items-center justify-between text-xs text-ink/70">
      <div className="flex items-center gap-5">
        <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-granate" data-testid="topbar-email">
          <Mail className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{CONTACT.email}</span>
        </a>
        <a href={CONTACT.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-granate" data-testid="topbar-instagram">
          <InstagramIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{CONTACT.instagram}</span>
        </a>
      </div>
      <div className="flex items-center gap-3">
        <a href={CONTACT.waUrl} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-[#25D366] text-white grid place-items-center hover:scale-110 transition-transform" data-testid="topbar-whatsapp" aria-label="WhatsApp">
          <WhatsAppIcon className="w-4 h-4" />
        </a>
        <Link to="/contacto" className="hidden sm:inline-flex rounded-full border border-granate/40 px-4 py-1.5 font-brand text-[10px] font-semibold tracking-[0.18em] uppercase text-granate hover:bg-granate hover:text-white transition-colors" data-testid="topbar-contact-btn">
          Hablemos
        </Link>
      </div>
    </div>
  </div>
);

const EventosMenu = () => (
  <DropdownMenu>
    <DropdownMenuTrigger className="flex items-center gap-1 whitespace-nowrap font-brand text-[11px] tracking-[0.14em] uppercase text-ink/80 hover:text-granate outline-none" data-testid="nav-eventos-trigger">
      Eventos <ChevronDown className="w-3 h-3" />
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-white">
      {["eventos", ...SERVICES.eventos.children].map((k) => (
        <DropdownMenuItem key={k} asChild>
          <Link to={SERVICES[k].path} data-testid={`nav-sub-${k}`}>{k === "eventos" ? "Todos los eventos" : SERVICES[k].name}</Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const links = [["/", "Home"], ...["eventos", ...SERVICES.eventos.children, ...MAIN_SERVICES.slice(1)].map((k) => [SERVICES[k].path, SERVICES[k].name]), ["/catalogo", "Catálogo"]];
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger className="xl:hidden p-2" data-testid="mobile-menu-btn" aria-label="Menú"><Menu className="w-6 h-6" /></SheetTrigger>
      <SheetContent className="bg-cream">
        <SheetTitle className="font-display text-3xl font-normal">Menú</SheetTitle>
        <nav className="mt-8 flex flex-col gap-4">
          {links.map(([to, label]) => (
            <NavLink key={to} to={to} end onClick={() => setOpen(false)} className={navCls} data-testid={`mobile-nav-${to.replace(/\//g, "-") || "home"}`}>{label}</NavLink>
          ))}
          <Link to="#cita" onClick={() => setOpen(false)} className="btn-shine mt-4 rounded-full bg-granate text-white text-center py-3 font-brand text-xs tracking-[0.2em] uppercase" data-testid="mobile-agenda-btn">Agenda una cita</Link>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50" data-testid="site-header">
    <TopBar />
    <div className="bg-cream/90 backdrop-blur-xl shadow-[0_10px_30px_-20px_rgba(30,20,24,0.25)]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 h-[60px] flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-5">
          <NavLink to="/" end className={navCls} data-testid="nav-home">Home</NavLink>
          <EventosMenu />
          {MAIN_SERVICES.slice(1).map((k) => (
            <NavLink key={k} to={SERVICES[k].path} className={navCls} data-testid={`nav-${k}`}>{SERVICES[k].name}</NavLink>
          ))}
          <NavLink to="/catalogo" className={navCls} data-testid="nav-catalogo">Catálogo</NavLink>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="#cita" className="btn-shine hidden xl:inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-granate text-white px-5 py-2.5 font-brand text-[11px] font-semibold tracking-[0.18em] uppercase shadow-lg shadow-granate/30 hover:bg-[#7f1625] transition-colors" data-testid="header-agenda-btn">
            <CalendarCheck className="w-4 h-4" /> Agenda una cita
          </Link>
          <MobileMenu />
        </div>
      </div>
    </div>
  </header>
);
